import Button from '@carvana/ds-button';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import styles from './vehicle-card.module.css';
import { financeTotalLabel, formatCurrency, getNonFinanceLabel } from './vehicle-card-helpers';

export interface VehicleSummary {
  vehicleId?: number;
  imageUrl: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  listingPrice: number;
  mileage: number;
  purchaseType: 'finance' | 'cash' | 'saf';
  // Finance only
  monthlyPayment?: number;
  cashDue?: number;
  // Cash only
  totalPurchasePrice?: number;
  hasCreditFromPreviousOrder?: boolean;
}

interface VehicleCardProps {
  summary: VehicleSummary;
  onViewOrderDetails?: () => void;
}

const VehicleCard: React.FC<VehicleCardProps> = ({ summary, onViewOrderDetails }) => {
  const { imageUrl, year, make, model, trim, purchaseType, monthlyPayment, cashDue } = summary;

  const totalDisplay =
    purchaseType === 'finance' ? financeTotalLabel(cashDue ?? 0) : getNonFinanceLabel(summary);

  return (
    <div className={styles.card}>
      <div className={styles.content}>
        <div className={styles.mediaWrap}>
          <img src={imageUrl} alt={`${year} ${make} ${model}`} className={styles.media} />
        </div>

        <div className={styles.info}>
          <div className={styles.ymmt}>
            <p className={styles.vehicleName}>
              {year} {make} {model}
            </p>
            <p className={styles.trimText}>{trim}</p>
          </div>

          <div className={styles.priceDetails}>
            <div className={styles.purchaseStats}>
              {purchaseType === 'finance' && (
                <div className={styles.statStacked}>
                  <span className={styles.statStackedLabel}>Payment</span>
                  <div className={styles.statStackedAmountInline}>
                    <span className={styles.statStackedAmount}>
                      {formatCurrency(monthlyPayment ?? 0)}
                    </span>
                    <span className={styles.statStackedAmountSuffix}>/mo</span>
                  </div>
                </div>
              )}
              <div className={styles.statStacked}>
                <span className={styles.statStackedLabel}>{totalDisplay.label}</span>
                <span className={styles.statStackedAmount}>
                  {formatCurrency(totalDisplay.amount)}
                </span>
              </div>
            </div>
            <Button
              className={styles.reviewOrderButton}
              size="xs"
              variant="secondary"
              utility
              onClick={onViewOrderDetails}
              data-analytics-id="checkout-vehicle-card-review-order"
            >
              Review order <ChevronRight size={16} color="currentColor" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleCard;
